import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faSave,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import StateManagedSelect from "react-select";
import axios from "axios";
import { API_URL } from "../../config";
import Swal from "sweetalert2";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper } from "../../helpers/permission.js";

const CATEGORY_OPTIONS = [
  { value: "portfolio_notification", label: "Portfolio Notification", table: "tbl_portfolio_notification_template" },
  { value: "campaign", label: "Campaign", table: "tbl_template_bulk_campaign" },
  { value: "mail", label: "Mail", table: "tbl_template_mail" },
  { value: "push_notification", label: "Push Notification", table: "tbl_template_pushnotification" },
  { value: "sms", label: "SMS", table: "tbl_template_sms" },
  { value: "whatsapp", label: "WhatsApp Notification", table: "tbl_template_whatsapp_notification" },
];

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const ConfigurationsTemplatesList = () => {
  const [templatesList, setTemplatesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  // 🔎 Inline Search Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [templateFilter, setTemplateFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // 🪟 Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formCategory, setFormCategory] = useState(null);
  const [formTemplate, setFormTemplate] = useState([]);
  const [formTemplateOptions, setFormTemplateOptions] = useState([]);
  const [formStatus, setFormStatus] = useState(STATUS_OPTIONS[0]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/configurationsTemplates/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            limit: 300,
          },
        }
      );

      const items = response.data?.data?.data || response.data?.data || [];
      setTemplatesList(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err.message || "Failed to load configurations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurations();
  }, []);

  // Fetch templates dynamically for selected category
  const fetchTemplatesForCategory = async (categoryValue) => {
    if (!categoryValue) {
      setFormTemplateOptions([]);
      return [];
    }

    try {
      setTemplateLoading(true);
      const response = await axios.get(
        `${API_URL}settings/configurationsTemplates/templates`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: { category: categoryValue },
        }
      );

      const rawTemplates = response.data?.data || [];
      const formatted = rawTemplates.map((t) => ({
        value: Number(t.id),
        label: t.name || `Template #${t.id}`,
      }));

      setFormTemplateOptions(formatted);
      return formatted;
    } catch (err) {
      console.error("Error fetching templates for category:", err);
      showToast.error("Failed to load templates for selected category");
      setFormTemplateOptions([]);
      return [];
    } finally {
      setTemplateLoading(false);
    }
  };

  // Open Modal for Add
  const handleOpenAddModal = () => {
    setEditingId(null);
    setFormCategory(null);
    setFormTemplate([]);
    setFormTemplateOptions([]);
    setFormStatus(STATUS_OPTIONS[0]);
    setShowModal(true);
  };

  // Open Modal for Edit
  const handleOpenEditModal = async (item) => {
    setEditingId(item.id);
    setShowModal(true);
    setModalLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}settings/configurationsTemplates/get/${item.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data?.data || item;

      const matchedCat =
        CATEGORY_OPTIONS.find((c) => c.value === data.category) || {
          value: data.category,
          label: data.category_label || data.category,
        };
      setFormCategory(matchedCat);

      const matchedStatus =
        STATUS_OPTIONS.find((s) => s.value === Number(data.status)) ||
        STATUS_OPTIONS[0];
      setFormStatus(matchedStatus);

      if (data.category) {
        const templates = await fetchTemplatesForCategory(data.category);
        if (data.template_id) {
          const idList = String(data.template_id)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          const matchedTpls = idList.map((id) => {
            const found = templates.find((t) => Number(t.value) === Number(id));
            return found || { value: Number(id), label: `Template #${id}` };
          });
          setFormTemplate(matchedTpls);
        } else {
          setFormTemplate([]);
        }
      }
    } catch (err) {
      console.error("Error loading configuration details:", err);
      showToast.error("Failed to load configuration details");
    } finally {
      setModalLoading(false);
    }
  };

  const handleCategoryChange = (catOption) => {
    setFormCategory(catOption);
    setFormTemplate([]);
    if (catOption?.value) {
      fetchTemplatesForCategory(catOption.value);
    } else {
      setFormTemplateOptions([]);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormCategory(null);
    setFormTemplate([]);
    setFormTemplateOptions([]);
  };

  // Submit Modal Form
  const handleModalSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formCategory?.value) {
      showToast.error("Please select a Category");
      return;
    }

    if (!formTemplate || formTemplate.length === 0) {
      showToast.error("Please select at least one Template");
      return;
    }

    try {
      setSubmitting(true);

      const templateIds = Array.isArray(formTemplate)
        ? formTemplate.map((t) => t.value).join(",")
        : String(formTemplate.value || "");

      const payload = {
        category: formCategory.value,
        template_id: templateIds,
        status: formStatus?.value !== undefined ? formStatus.value : 1,
      };

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      let response;
      if (editingId) {
        response = await axios.put(
          `${API_URL}settings/configurationsTemplates/update/${editingId}`,
          payload,
          config
        );
      } else {
        response = await axios.post(
          `${API_URL}settings/configurationsTemplates/save`,
          payload,
          config
        );
      }

      if (response.data?.success !== false) {
        showToast.success(
          response.data?.message ||
            `Configuration template ${editingId ? "updated" : "saved"} successfully!`
        );
        handleCloseModal();
        fetchConfigurations();
      } else {
        showToast.error(response.data?.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Error submitting configuration:", err);
      showToast.error(
        err.response?.data?.message || "An error occurred while saving"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // 🔎 Apply Column Search Filters
  const filteredList = useMemo(() => {
    return templatesList.filter((item) => {
      if (
        categoryFilter &&
        !(item.category_label || item.category || "")
          .toLowerCase()
          .includes(categoryFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        templateFilter &&
        !(item.template_name || `Template #${item.template_id}` || "")
          .toLowerCase()
          .includes(templateFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [templatesList, categoryFilter, templateFilter]);

  useEffect(() => {
    if (!templatesList.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [categoryFilter, templateFilter, templatesList]);

  // 📑 Update pagination
  useEffect(() => {
    setTotal(filteredList.length);
    setPageCount(Math.ceil(filteredList.length / limit) || 1);
    setPage(1);
  }, [filteredList, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedData = filteredList.slice((page - 1) * limit, page * limit);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this configuration template!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(
            `${API_URL}settings/configurationsTemplates/delete/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data?.success !== false) {
            Swal.fire(
              "Deleted!",
              response.data?.message || "Configuration template deleted.",
              "success"
            );
            fetchConfigurations();
          } else {
            Swal.fire("Error!", response.data?.message || "Failed to delete", "error");
          }
        } catch (err) {
          Swal.fire(
            "Error!",
            err.response?.data?.message || "Failed to delete",
            "error"
          );
        }
      }
    });
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Top Header Row */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Configurations Templates</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            {/* <PermissionWrapper
              module="settings_configurations_templates"
              action="add"
            >
              <button
                type="button"
                className="btn btn-default btn-sm me-2"
                style={{ marginRight: "10px" }}
                onClick={handleOpenAddModal}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Configuration Template
              </button>
            </PermissionWrapper> */}

            {!loading && templatesList.length > 0 && (
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Panel with Sticky Table */}
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="table-responsive sticky-table-container">
            <style>
              {`
                .sticky-config-table thead tr:first-child th {
                  position: sticky;
                  top: 0;
                  z-index: 20;
                  background: #f8f9fa;
                  padding: 8px 6px !important;
                  font-size: 12px;
                }
                .sticky-config-table thead tr:nth-child(2) th {
                  position: sticky;
                  top: 32px;
                  z-index: 19;
                  background: white;
                  padding: 4px 6px !important;
                }
                .sticky-table-container {
                  max-height: calc(100vh - 152px);
                  overflow: auto;
                }
                .sticky-config-table th {
                  vertical-align: top;
                }
                .sticky-config-table .input-sm {
                  height: 24px !important;
                  padding: 2px 6px !important;
                  font-size: 11px !important;
                  line-height: 1.2;
                  border-radius: 3px;
                }
                .sticky-config-table input::placeholder {
                  font-size: 11px !important;
                  color: #999;
                }
                .sticky-config-table td {
                  padding: 8px 6px !important;
                  font-size: 12px;
                  vertical-align: middle;
                }
              `}
            </style>

            <table className="table m-b-0 sticky-config-table">
              <thead>
                {/* 1st Header Row: Column Titles */}
                <tr>
                  <th style={{ textAlign: "center", width: "40px" }}>#</th>
                  <th>Category</th>
                  <th>Template Name</th>
                  <th style={{ width: "80px", textAlign: "center" }}>Action</th>
                </tr>

                {/* 2nd Header Row: Search Filters */}
                <tr>
                  <th style={{ textAlign: "center" }}></th>
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search category..."
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      disabled={loading}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search template..."
                      value={templateFilter}
                      onChange={(e) => setTemplateFilter(e.target.value)}
                      disabled={loading}
                    />
                  </th>
                  <th style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      onClick={() => {
                        setCategoryFilter("");
                        setTemplateFilter("");
                      }}
                      title="Clear filters"
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading || filterLoading ? (
                  Array.from({ length: 8 }).map((_, rowIndex) => (
                    <tr key={rowIndex} className="skeleton-row">
                      {Array.from({ length: 4 }).map((_, colIndex) => (
                        <td key={colIndex}>
                          <div className="skeleton-box"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{item.category_label || item.category}</td>
                      <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                        {item.template_name || `Template #${item.template_id}`}
                      </td>
                      <td style={{ width: "80px" }}>
                        <div className="d-flex gap-2">
                          <PermissionWrapper
                            module="settings_configurations_templates"
                            action="edit"
                          >
                            <button
                              type="button"
                              className="btn btn-warning btn-xs"
                              title="Edit"
                              style={{ marginRight: "5px" }}
                              onClick={() => handleOpenEditModal(item)}
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </PermissionWrapper>

                          <PermissionWrapper
                            module="settings_configurations_templates"
                            action="delete"
                          >
                            <button
                              type="button"
                              className="btn btn-danger btn-xs"
                              title="Delete"
                              onClick={() => handleDelete(item.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      {templatesList.length === 0
                        ? "No records found. Add Configuration Template to get started."
                        : "No templates match your search criteria."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 🪟 Modal Form */}
        {showModal && (
          <div
            className="modal fade in show"
            style={{
              display: "block",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1050,
            }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="col-md-11 p-0">
                    <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                      {editingId ? "Edit Configuration Template" : "Add Configuration Template"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={handleCloseModal}
                  >
                    &times;
                  </button>
                  <div className="clearfix"></div>
                </div>

                <div className="modal-body">
                  {modalLoading ? (
                    <div className="text-center p-4">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <form onSubmit={handleModalSubmit}>
                      <div className="row">
                        {/* 1st Dropdown: Category */}
                        <div className="form-group col-md-6 mb-3">
                          <label className="control-label">
                            Category <span className="text-danger">*</span>
                          </label>
                          <StateManagedSelect
                            options={CATEGORY_OPTIONS}
                            value={formCategory}
                            onChange={handleCategoryChange}
                            placeholder="-- Select Category --"
                            isClearable={false}
                            className="basic-single"
                            classNamePrefix="select"
                          />
                        </div>

                        {/* 2nd Dropdown: Template (Multi-Select) */}
                        <div className="form-group col-md-6 mb-3">
                          <label className="control-label">
                            Template <span className="text-danger">*</span>
                            {templateLoading && (
                              <span className="text-primary ml-2" style={{ fontSize: "12px" }}>
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                              </span>
                            )}
                          </label>
                          <StateManagedSelect
                            isMulti={true}
                            options={formTemplateOptions}
                            value={formTemplate}
                            onChange={setFormTemplate}
                            placeholder={
                              !formCategory
                                ? "Select a category first"
                                : templateLoading
                                ? "Loading templates..."
                                : formTemplateOptions.length === 0
                                ? "No templates found for this category"
                                : "-- Select Template(s) --"
                            }
                            isDisabled={!formCategory || templateLoading}
                            isClearable={true}
                            className="basic-multi-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </div>

                      {/* Status */}
                      <div className="form-group mb-3">
                        <label className="control-label">Status</label>
                        <StateManagedSelect
                          options={STATUS_OPTIONS}
                          value={formStatus}
                          onChange={setFormStatus}
                          placeholder="Select Status"
                          isClearable={false}
                          className="basic-single"
                          classNamePrefix="select"
                        />
                      </div>

                      <div className="form-group text-right mt-3 mb-0">
                        <button
                          type="submit"
                          className="btn btn-primary mx-2"
                          style={{ marginRight: "8px" }}
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSave} /> {editingId ? "Update" : "Submit"}
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={handleCloseModal}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigurationsTemplatesList;
